import Spin from 'antd/lib/spin';
import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import withStyles from 'react-jss';
import { fetchQuotes } from 'src/api/shipmentExecution';
import { StylesProps } from 'src/Library/Types';
import { getVendorTypeFromQuoteField } from '../../../SE.constants';
import { QuoteType } from '../../../SE.interfaces';
import SEQuotes from './SEQuotes';
import { SEQuoteCandidatesContext, SEQuoteSelectionContext } from './SEQuoteSelectionContext';
import { SEQuotePanelStyles } from './SEQuotePanel.styles';

const styles = () => ({ ...SEQuotePanelStyles() });

interface SEQuotePanelProps extends StylesProps<ReturnType<typeof styles>> {
  field?: any;
}

/**
 * Renders quote selection for ONE vendor type (one RFQ_QUOTE field). Fetches that vendor's quotes,
 * persists the chosen quote into its own field, and reports the selection to the task-level footer.
 */
const SEQuotePanel = (props: SEQuotePanelProps): React.ReactElement => {
  const { classes, field } = props;
  const { shipmentExecutionId, sourceObjectId, name: fieldName, fieldId } = field?.extraData || {};
  const seId = shipmentExecutionId || sourceObjectId;
  const vendorType = getVendorTypeFromQuoteField(field?.extraData);
  // Vendor-type-first so the task-level tab strip can match this selection to its tab
  // (SEQuoteTaskTabs derives the same key from tabMeta via getVendorTypeFromQuoteField).
  const fieldKey = vendorType || fieldName || fieldId || '';

  const { reportSelection } = useContext(SEQuoteSelectionContext);
  const { reportCandidates, savedCandidates } = useContext(SEQuoteCandidatesContext);

  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [quotes, setQuotes] = useState<QuoteType[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [selectedQuote, setSelectedQuote] = useState<QuoteType | undefined>(undefined);
  const [isInitialAssignment, setIsInitialAssignment] = useState<boolean>(true);

  const setData = async () => {
    if (!seId || !vendorType) return;
    setIsFetching(true);
    const response = await fetchQuotes({ id: seId, vendorType });
    if (response.isSuccess) {
      setQuotes(response.data?.quotes || []);
      setErrorMessage('');
    } else {
      setQuotes([]);
      setErrorMessage(response.errorMessage || 'Unable to fetch quotes.');
    }
    setIsFetching(false);
  };

  useEffect(() => {
    if (field?.disabled) return;
    setData();
  }, [seId, vendorType, field?.disabled]);

  // Snapshot this vendor type's full displayed list to the task modal, which writes it (merged across
  // all included vendor types) into Quote Candidates (SMF427) on submit. Skip the read-only (approval)
  // view — there we render from the saved snapshot, we don't re-capture.
  useEffect(() => {
    if (field?.disabled) return;
    reportCandidates(fieldKey, quotes);
  }, [quotes, fieldKey, field?.disabled]);

  // Hydrate the previously-saved selection (single RankedQuote stored on this field).
  useEffect(() => {
    if (field?.value && isInitialAssignment) {
      setSelectedQuote(field.value);
      setIsInitialAssignment(false);
    }
  }, [field?.value]);

  // Persist to the field + report to the shared footer whenever the selection changes.
  useEffect(() => {
    if (field) {
      field.setValue(selectedQuote);
    }
    reportSelection(fieldKey, selectedQuote);
  }, [selectedQuote]);

  const onQuoteSelection = (quote: QuoteType) => setSelectedQuote(quote);
  const resetQuote = () => setSelectedQuote(undefined);

  if (isFetching) {
    return <div className={classes.loader}><Spin /></div>;
  }

  // Read-only (approval, SMT72) view: show ALL quotes that were available at selection time — this
  // vendor type's slice of the frozen Quote Candidates snapshot (SMF427) — with the chosen quote
  // highlighted. Fall back to the single saved selection for snapshots taken before SMF427 existed.
  const savedForVendor = savedCandidates?.filter((quote) => quote?.vendorType === vendorType) || [];
  const readOnlyQuotes = savedForVendor.length ? savedForVendor : (field?.value ? [field.value] : []);
  const quoteList = field?.disabled ? readOnlyQuotes : quotes;

  return (
    <div>
      {errorMessage && <div className={classes.errorText}>{errorMessage}</div>}
      <SEQuotes
        selectedQuote={selectedQuote}
        quoteList={quoteList}
        onQuoteSelection={onQuoteSelection}
        resetQuote={resetQuote}
        disabled={field?.disabled}
      />
    </div>
  );
};

export default withStyles(styles)(SEQuotePanel);
